import combinedHistoryData from '../data/history/index.js';

/**
 * TeableQuestionService.js
 * Client-side service to fetch questions from Teable API via server proxy.
 * Falls back to local window.questionsData when API is unavailable.
 */

// Category key → Teable "Kategori" display name mapping
const CATEGORY_DISPLAY = {
    tarih: 'Tarih',
    cografya: 'Coğrafya',
    gundem: 'Gündem',
    din: 'Din',
    edebiyat: 'Edebiyat',
    fen_bilimleri: 'Fen Bilimleri',
    karisik: 'Karışık',
    psikoloji: 'Psikoloji',
    sinema: 'Sinema',
    siyaset: 'Siyaset',
    sosyoloji: 'Sosyoloji',
    spor: 'Spor',
    mitoloji: 'Mitoloji',
    'Tarihte Bugün': 'Tarihte Bugün'
};

// Difficulty key → Teable "Alt_Tur_veya_Zorluk" display name mapping
const DIFFICULTY_DISPLAY = {
    kolay: 'Kolay',
    orta: 'Orta',
    zor: 'Zor',
    turkiye: 'Türkiye',
    dunya: 'Dünya'
};

window.TeableQuestionService = {

    /**
     * Fetch questions for Normal Mode (category + difficulty).
     * @param {string} categoryKey - Internal key (e.g. 'tarih', 'cografya')
     * @param {string} difficultyKey - Internal key (e.g. 'kolay', 'orta', 'zor')
     * @returns {Promise<Array>} Array of question objects {id, text, options[], correct}
     */
    async fetchQuestions(categoryKey, difficultyKey) {
        // ALWAYS use local fallback directly to skip broken API
        return this._localFallback(categoryKey, difficultyKey);
    },

    /**
     * Fetch questions by category only (for Wheel / Online).
     * @param {string} categoryKey - Internal key
     * @returns {Promise<Array>}
     */
    async fetchByCategory(categoryKey) {
        return this._localFallback(categoryKey, null);
    },

    /**
     * Fetch Gündem questions.
     * @param {string} subType - 'turkiye' or 'dunya'
     * @returns {Promise<Array>}
     */
    async fetchGundemQuestions(subType) {
        return this._localFallback('gundem', null);
    },

    /**
     * Fetch Tarihte Bugün questions for today's date.
     * @returns {Promise<Array>}
     */
    async fetchTarihteBugunQuestions() {
        console.log(`📡 [TeableService] Fetching Tarihte Bugün (Local File Mode)...`);
        return this._localFallbackTarihteBugun();
    },

    /**
     * Fetch GADDAR questions using the special tag.
     * @returns {Promise<Array>}
     */
    async fetchGaddarQuestions() {
        console.log(`📡 [TeableService] Fetching GADDAR questions via Teable tag...`);
        try {
            const params = new URLSearchParams({
                category: 'Karışık',
                date: 'GADDAR_MOD',
                take: '100'
            });
            const response = await fetch(`/api/questions?${params}`);
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const data = await response.json();
            if (data.success && data.questions && data.questions.length > 0) {
                console.log(`✅ [TeableService] Got ${data.questions.length} Gaddar questions from Teable`);
                return data.questions;
            }
            throw new Error('No Gaddar questions in Teable');
        } catch (e) {
            console.warn(`⚠️ [TeableService] Gaddar Teable hatası, yerel fallback...`);
            return this._localFallback('gaddar', 'orta'); // Use local gaddar/orta fallback
        }
    },

    /**
     * Fetch WEEKLY questions using the special tag.
     * @returns {Promise<Array>}
     */
    async fetchWeeklyQuestions() {
        console.log(`📡 [TeableService] Fetching WEEKLY questions via Teable tag...`);
        try {
            const params = new URLSearchParams({
                category: 'Karışık',
                date: 'WEEKLY_MOD',
                take: '100'
            });
            const response = await fetch(`/api/questions?${params}`);
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const data = await response.json();
            if (data.success && data.questions && data.questions.length > 0) {
                console.log(`✅ [TeableService] Got ${data.questions.length} Week questions from Teable`);
                return data.questions;
            }
            throw new Error('No Week questions in Teable');
        } catch (e) {
            console.warn(`⚠️ [TeableService] Week Teable hatası, yerel fallback...`);
            return this._localFallback('haftalik', 'orta');
        }
    },

    /**
     * Fetch Mythology questions.
     * @param {string} subCategory - 'Türk Mitolojisi', 'Yunan Mitolojisi', etc.
     * @returns {Promise<Array>}
     */
    async fetchMythologyQuestions(subCategory) {
        // Map subCategory (e.g. 'turk', 'yunan') to the flat key ('mitoloji_turk', 'mitoloji_yunan')
        let key = 'mitoloji_' + subCategory;
        if (!window.questionsData[key]) key = 'mitoloji_turk'; // Fallback
        return this._localFallback(key, null);
    },
    /**
     * Fetch Ataturk Questions (Milli Mücadele, Çağdaş Türkiye, etc.)
     * @param {string} subType - 'milli_mucadele', 'cagdas_turkiye', 'kim_taniyor'
     * @returns {Promise<Array>}
     */
    async fetchAtaturkQuestions(subType) {
        // Map internal keys to Teable values
        const SUBTYPE_MAP = {
            'milli_mucadele': 'Milli Mücadele',
            'cagdas_turkiye': 'Çağdaş Türkiye',
            'kim_taniyor': "Atatürk'ü Kim Tanıyor?" // Best guess based on user prompt context, or can use 'Kim Tanıyor'
        };

        const difficulty = SUBTYPE_MAP[subType] || subType;
        console.log(`📡 [TeableService] Fetching Atatürk Questions: ${difficulty}`);

        try {
            const params = new URLSearchParams({
                category: 'Atatürk',
                difficulty: difficulty,
                take: '20' // Fetch 20, we can pick 10 random
            });
            const response = await fetch(`/api/questions?${params}`);

            if (!response.ok) throw new Error(`HTTP ${response.status}`);

            const data = await response.json();
            if (data.success && data.questions && data.questions.length > 0) {
                console.log(`✅ [TeableService] Got ${data.questions.length} Atatürk questions`);

                // Shuffle and pick 10
                const shuffled = data.questions.sort(() => 0.5 - Math.random());
                return shuffled.slice(0, 10);
            }

            throw new Error('API boş döndü');
        } catch (e) {
            console.warn(`⚠️ [TeableService] Atatürk API hatası: ${e.message}`);
            return this._localFallback(subType, null);
        }
    },

    /**
     * Fetch Ataturk Quotes
     * @returns {Promise<Array>}
     */
    async fetchAtaturkQuotes() {
        console.log(`📡 [TeableService] Fetching Ataturk Quotes...`);
        try {
            const response = await fetch('/api/quotes');
            if (!response.ok) throw new Error(`HTTP ${response.status}`);

            const data = await response.json();
            if (data.success && data.quotes && data.quotes.length > 0) {
                console.log(`✅ [TeableService] Got ${data.quotes.length} quotes`);
                return data.quotes;
            }
            throw new Error('No quotes returned');
        } catch (e) {
            console.warn(`⚠️ [TeableService] Quotes Error: ${e.message}`);
            return this._localFallbackQuotes();
        }
    },

    _localFallbackQuotes() {
        return [
            "Yurtta sulh, cihanda sulh.",
            "Hayatta en hakiki mürşit ilimdir.",
            "Egemenlik, kayıtsız şartsız milletindir.",
            "Ne mutlu Türk'üm diyene!",
            "Beni görmek demek mutlaka yüzümü görmek değildir."
        ];
    },

    // ========== FALLBACK METHODS ==========

    /**
     * Fallback: get questions from local window.questionsData[category][difficulty]
     */
    _localFallback(categoryKey, difficultyKey) {
        console.log(`🔄 [Fallback] Yerel veri (Tek Havuz): ${categoryKey}`);
        const data = window.questionsData;
        if (data && data[categoryKey]) {
            // Flatten the pool and get 10 random questions
            let pool = data[categoryKey];
            if (Array.isArray(pool)) {
                // Shuffle and pick 10
                return pool.sort(() => 0.5 - Math.random()).slice(0, 10);
            }
        }
        console.error(`❌ [Fallback] Yerel veri de bulunamadı: ${categoryKey}`);
        return [];
    },

    /**
     * Fallback: get ALL questions from a category (all difficulties merged)
     */
    _localFallbackAll(categoryKey) {
        console.log(`🔄 [Fallback] Yerel veri (tümü): ${categoryKey}`);
        const data = window.questionsData;
        if (data && data[categoryKey]) {
            let pool = [];
            Object.values(data[categoryKey]).forEach(arr => {
                if (Array.isArray(arr)) pool = pool.concat(arr);
            });
            return pool;
        }
        return [];
    },

    /**
     * Fallback: Tarihte Bugün from local history data
     */
    _localFallbackTarihteBugun() {
        console.log(`🔄 [Fallback] Yerel Tarihte Bugün verisi (File Mode)`);
        const now = new Date();
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const dd = String(now.getDate()).padStart(2, '0');
        const key = `${mm}-${dd}`;
        const dayData = combinedHistoryData[key];
        if (dayData && dayData.questions) {
            return dayData.questions.map((q, i) => ({
                id: `hist_${key}_${i}`,
                text: q.text,
                options: q.options,
                correct: q.correct
            }));
        }
        return [];
    }
};
